// =============================================================================
// VandieZ agent-web -- Dashboard (/)
// =============================================================================
// AG-11 (p64). The nav has had a "Dashboard" link since it was written and
// it pointed at `MyOwnersPage` -- a list of the owners an agent works for.
// Useful, but not a dashboard, and not what the label promised: an agent
// logging in landed on a roster instead of on the state of their business.
//
// Everything here comes from endpoints that already existed
// (`/broker/dashboard`, `/broker/loads`). The gap was never the data.
//
// THE CHART IS A LOAD-STATUS COMPOSITION, not margin-over-time, and that is
// a deliberate limit rather than a shortcut. `/broker/reports` returns two
// windows to compare -- a current one and a previous one of the same length
// -- and no series. Rendering a trend line from two points, or from month-
// to-date totals, would be drawing a shape the data does not contain. A
// composition bar is the honest chart available today; a real trend needs a
// series endpoint, which is backend work.
// =============================================================================

import { AlertCircle, Loader2, RefreshCcw, Users } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { brokerApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { BrokerDashboard, BrokerLoadRow } from "@/types/broker";

function formatINR(v: string | number): string {
  const n = typeof v === "string" ? Number.parseFloat(v) : v;
  if (Number.isNaN(n)) {
    return "—";
  }
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "warning";
}): React.JSX.Element {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p
          className={
            tone === "warning"
              ? "text-2xl font-semibold text-danger-subtle-foreground"
              : "text-2xl font-semibold"
          }
        >
          {value}
        </p>
        {hint !== undefined ? (
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/// A stacked composition bar built from divs. No chart dependency: the shape
/// is four proportions of one total, which CSS expresses directly, and
/// pulling in a charting library for it would cost more bytes than the whole
/// page.
function StatusComposition({
  d,
}: {
  d: BrokerDashboard;
}): React.JSX.Element {
  const segments: { label: string; value: number; cls: string }[] = [
    { label: "Active", value: d.active_loads, cls: "bg-primary" },
    { label: "Completed", value: d.completed_loads, cls: "bg-success-subtle" },
    { label: "Cancelled", value: d.cancelled_loads, cls: "bg-danger-subtle" },
    { label: "Postponed", value: d.postponed_loads, cls: "bg-warning-subtle" },
  ];
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No loads yet. This fills in as you take work.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={segments
          .filter((s) => s.value > 0)
          .map((s) => `${s.label}: ${s.value}`)
          .join(", ")}
      >
        {segments.map((s) =>
          s.value === 0 ? null : (
            <div
              key={s.label}
              className={s.cls}
              style={{ width: `${(s.value / total) * 100}%` }}
            />
          ),
        )}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {segments.map((s) => (
          <li key={s.label} className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${s.cls}`} />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="font-medium tabular-nums">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DashboardPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [dashboard, setDashboard] = useState<BrokerDashboard | null>(null);
  const [active, setActive] = useState<BrokerLoadRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [d, l] = await Promise.all([
          brokerApi.dashboard(accessToken, signal),
          brokerApi.loads(accessToken, { status: "active" }, signal),
        ]);
        setDashboard(d);
        setActive(l.items);
        setError(null);
      } catch (e) {
        if (signal?.aborted === true) {
          return;
        }
        setError(
          e instanceof ApiError ? e.problem.detail : "Unexpected error.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => {
      ac.abort();
    };
  }, [load]);

  return (
    <>
      <AppHeader title="Dashboard" />
      <main className="mx-auto max-w-6xl space-y-6 p-4 pb-10">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Your business today</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <RefreshCcw className="mr-1 h-3 w-3" />
            )}
            Refresh
          </Button>
        </div>

        {error !== null ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {dashboard === null ? (
          loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : null
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Active loads" value={dashboard.active_loads} />
              <StatCard
                label="Margin this month"
                value={formatINR(dashboard.month_to_date_net_margin_inr)}
                hint={`${dashboard.month_to_date_load_count} loads`}
              />
              {/* Loss-makers sit beside the margin, not inside a report. A
                  healthy total can hide several, and those are the ones
                  worth looking at today. */}
              <StatCard
                label="Sold below cost"
                value={dashboard.month_to_date_loss_making_count}
                hint="this month"
                {...(dashboard.month_to_date_loss_making_count > 0
                  ? { tone: "warning" as const }
                  : {})}
              />
              <StatCard
                label="Drivers on roster"
                value={dashboard.roster_driver_count}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Load outcomes</CardTitle>
                <CardDescription>
                  Every load you have taken, by where it ended up.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <StatusComposition d={dashboard} />
              </CardContent>
            </Card>
          </>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Active loads</CardTitle>
            <CardDescription>Work in flight right now.</CardDescription>
          </CardHeader>
          <CardContent>
            {active === null ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : active.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing in flight.{" "}
                <Link className="text-primary underline" to="/marketplace">
                  Find work on the marketplace
                </Link>
                .
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Lane</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Pricing</th>
                      <th className="py-2 font-medium">Customer price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.map((l) => (
                      <tr key={l.load_id} className="border-b last:border-0">
                        <td className="py-2 pr-4">
                          {l.pickup_district} → {l.drop_district}
                        </td>
                        <td className="py-2 pr-4">
                          {l.status.replace(/_/g, " ")}
                        </td>
                        <td className="py-2 pr-4">
                          {l.pricing_model.replace(/_/g, " ")}
                        </td>
                        <td className="py-2 tabular-nums">
                          {l.customer_price_inr === null
                            ? "—"
                            : formatINR(l.customer_price_inr)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-sm text-muted-foreground">
          <Users className="mr-1 inline h-3.5 w-3.5" />
          Acting for a vehicle owner?{" "}
          <Link className="text-primary underline" to="/owners">
            See the owners you work for
          </Link>
          .
        </p>
      </main>
    </>
  );
}
